/**
 * 수수료 드라이브 고정 헤더 포맷 — 레거시 `commissionUploadValidation.js` 간소화
 */
import * as XLSX from 'xlsx';
import { normalizeCommissionDrugCode } from '@/pages/settlement-fees/commissionDrugCode';

export const COMMISSION_DRIVE_HEADERS = [
  '년월',
  '제약사',
  '제품명',
  '규격_유통단위',
  '제품코드',
  '보험가',
  '제약사수수료율',
  '비고1(제약사 참고사항)',
  '품절여부',
  '품절(From)',
  '품절(To)',
  '신규금지',
  '정산(From)',
  '정산(To)',
  '인센티브',
  '인센티브_메모',
  '주성분코드',
] as const;

export const DRIVE_COL = {
  yearMonth: 0,
  pharmaName: 1,
  drugName: 2,
  drugCode: 4,
  price: 5,
  commissionRate: 6,
  commissionNote: 7,
  outOfStock: 8,
  outOfStockFrom: 9,
  outOfStockTo: 10,
  filteringDisabled: 11,
  incentive: 14,
  incentiveNote: 15,
} as const;

const normalizeHeader = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, '')
    .toLowerCase();

export const isDriveFormatHeader = (headerRow: unknown[]): boolean => {
  if (
    !headerRow?.length ||
    headerRow.length < COMMISSION_DRIVE_HEADERS.length
  ) {
    return false;
  }
  for (let i = 0; i < COMMISSION_DRIVE_HEADERS.length; i++) {
    if (
      normalizeHeader(headerRow[i]) !==
      normalizeHeader(COMMISSION_DRIVE_HEADERS[i])
    ) {
      return false;
    }
  }
  return true;
};

/** 상단 안내/필수 표시 행을 건너뛰고 실제 드라이브 헤더 위치를 찾는다. */
export const findDriveHeaderRowIndex = (rows: unknown[][]): number => {
  for (let i = 0; i < Math.min(rows.length, 60); i++) {
    if (isDriveFormatHeader(rows[i] ?? [])) return i;
  }
  return -1;
};

const parseRate = (value: unknown): number => {
  if (value == null || value === '') return 0;
  const num = Number(String(value).replace(/[^\d.]/g, ''));
  if (!Number.isFinite(num)) return 0;
  return num > 1 ? num : num * 100;
};

const padDriveRow = (row: unknown[]): string[] => {
  const padded = row.map((cell) => (cell == null ? '' : String(cell)));
  while (padded.length < COMMISSION_DRIVE_HEADERS.length) padded.push('');
  return padded.slice(0, COMMISSION_DRIVE_HEADERS.length);
};

export type DriveRow = string[];

export const parseDriveWorkbook = (
  workbook: XLSX.WorkBook,
): { rows: DriveRow[]; sheetName: string } | null => {
  for (const sheetName of workbook.SheetNames) {
    const sheetRows = XLSX.utils.sheet_to_json<unknown[]>(
      workbook.Sheets[sheetName],
      {
        header: 1,
        defval: null,
        blankrows: false,
      },
    );
    const headerIdx = findDriveHeaderRowIndex(sheetRows);
    if (headerIdx < 0) continue;

    const dataRows: DriveRow[] = [];
    for (let i = headerIdx + 1; i < sheetRows.length; i++) {
      const row = padDriveRow(sheetRows[i] ?? []);
      const drugCode = normalizeCommissionDrugCode(row[DRIVE_COL.drugCode]);
      const drugName = row[DRIVE_COL.drugName]?.trim();
      if (!drugCode && !drugName) continue;
      dataRows.push(row);
    }
    return { rows: dataRows, sheetName };
  }
  return null;
};

export const parseDriveFile = async (
  file: File,
): Promise<DriveRow[] | null> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: false });
  return parseDriveWorkbook(workbook)?.rows ?? null;
};

export const mergeDriveWithPreviousMonth = (
  previousRows: DriveRow[],
  extractedRows: DriveRow[],
  referenceYearMonth: string,
): { rows: DriveRow[]; matchedCount: number; appendedCount: number } => {
  const baseRows = previousRows.map((row) => {
    const copy = [...row];
    copy[DRIVE_COL.yearMonth] = referenceYearMonth;
    return copy;
  });

  const extractedByCode = new Map<string, DriveRow>();
  extractedRows.forEach((row) => {
    const code = normalizeCommissionDrugCode(row[DRIVE_COL.drugCode]);
    if (code) extractedByCode.set(code, row);
  });

  let matchedCount = 0;
  baseRows.forEach((row) => {
    const code = normalizeCommissionDrugCode(row[DRIVE_COL.drugCode]);
    const extracted = code ? extractedByCode.get(code) : undefined;
    if (!extracted) return;
    row[DRIVE_COL.pharmaName] =
      extracted[DRIVE_COL.pharmaName] || row[DRIVE_COL.pharmaName];
    row[DRIVE_COL.drugName] =
      extracted[DRIVE_COL.drugName] || row[DRIVE_COL.drugName];
    row[DRIVE_COL.price] = extracted[DRIVE_COL.price] || row[DRIVE_COL.price];
    row[DRIVE_COL.commissionRate] =
      extracted[DRIVE_COL.commissionRate] || row[DRIVE_COL.commissionRate];
    row[DRIVE_COL.commissionNote] =
      extracted[DRIVE_COL.commissionNote] || row[DRIVE_COL.commissionNote];
    matchedCount += 1;
    extractedByCode.delete(code);
  });

  let appendedCount = 0;
  extractedByCode.forEach((row) => {
    const copy = [...row];
    copy[DRIVE_COL.yearMonth] = referenceYearMonth;
    baseRows.push(copy);
    appendedCount += 1;
  });

  return { rows: baseRows, matchedCount, appendedCount };
};

export const driveRowsToExportSheet = (rows: DriveRow[]) => {
  const worksheet = XLSX.utils.aoa_to_sheet([
    [...COMMISSION_DRIVE_HEADERS],
    ...rows,
  ]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  return workbook;
};

export const driveRowToPreview = (row: DriveRow) => ({
  referenceYearMonth: row[DRIVE_COL.yearMonth],
  pharmaceuticalCompanyName: row[DRIVE_COL.pharmaName],
  drugCode: normalizeCommissionDrugCode(row[DRIVE_COL.drugCode]),
  drugName: row[DRIVE_COL.drugName],
  price: Number(String(row[DRIVE_COL.price]).replace(/,/g, '')) || 0,
  standardCommissionRate: parseRate(row[DRIVE_COL.commissionRate]),
  commissionNote: row[DRIVE_COL.commissionNote],
});
